import { useState, useRef, useEffect } from 'react'
import { MessageCircle, X, Send, Bot } from 'lucide-react'
import { GlassPanel } from './ui/glass/GlassPanel'
import { GlassBadge } from './ui/glass/GlassBadge'
import { cn } from '../lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: string
  role: 'user' | 'bot'
  text: string
  timestamp: Date
}

// ---------------------------------------------------------------------------
// Quick-reply options
// ---------------------------------------------------------------------------

const QUICK_REPLIES = [
  { label: 'How does MEV protection work?', key: 'how does mev protection work' },
  { label: 'Pricing plans?',                key: 'pricing' },
  { label: 'Connect my wallet',             key: 'connect' },
]

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 rounded-full bg-sky-400/70 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Individual message bubble
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'
  return (
    <div className={cn('flex gap-2 mb-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {!isUser && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
          <Bot className="w-3.5 h-3.5 text-sky-400" />
        </div>
      )}
      <div
        className={cn(
          'max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed',
          isUser
            ? 'bg-sky-500/20 border border-sky-500/30 text-white rounded-tr-sm'
            : 'bg-white/[0.07] border border-white/10 text-white/90 rounded-tl-sm'
        )}
      >
        {message.text}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export function SupportChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'bot',
      text: "Hey! I'm the MEV Shield assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to latest message
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isTyping, isOpen])

  function addMessage(role: 'user' | 'bot', text: string) {
    const msg: Message = {
      id: `${Date.now()}-${Math.random()}`,
      role,
      text,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, msg])
    return msg
  }

  async function fetchBotReply(currentMessages: Message[]) {
    setIsTyping(true)
    try {
      const token = localStorage.getItem('mev_shield_token')
      const payload = currentMessages.map((m) => ({
        role: m.role === 'bot' ? 'assistant' : 'user',
        content: m.text,
      }))
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: payload }),
      })
      const data = await res.json()
      addMessage('bot', data.reply ?? "Sorry, I couldn't get a response.")
    } catch {
      addMessage('bot', "Sorry, I'm having trouble connecting. Try again shortly.")
    } finally {
      setIsTyping(false)
    }
  }

  function handleSend(text: string) {
    const trimmed = text.trim()
    if (!trimmed) return
    const userMsg: Message = {
      id: `${Date.now()}-${Math.random()}`,
      role: 'user',
      text: trimmed,
      timestamp: new Date(),
    }
    setMessages((prev) => {
      const next = [...prev, userMsg]
      fetchBotReply(next)
      return next
    })
    setInputText('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(inputText)
    }
  }

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Chat window                                                         */}
      {/* ------------------------------------------------------------------ */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[340px] max-h-[520px] flex flex-col shadow-2xl">
          <GlassPanel padding="none" className="flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-primary flex items-center justify-center shadow-glow-sky">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-none">MEV Shield Support</p>
                  <GlassBadge variant="success" dot className="mt-0.5 text-[10px]">
                    Online
                  </GlassBadge>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0 max-h-[280px] scrollbar-thin scrollbar-thumb-white/10">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isTyping && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-sky-400" />
                  </div>
                  <div className="bg-white/[0.07] border border-white/10 rounded-2xl rounded-tl-sm">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick replies */}
            {messages.length <= 2 && !isTyping && (
              <div className="px-4 pb-2 flex flex-col gap-1.5">
                <p className="text-[10px] text-white/40 uppercase tracking-widest font-medium mb-0.5">
                  Quick answers
                </p>
                {QUICK_REPLIES.map((qr) => (
                  <button
                    key={qr.key}
                    onClick={() => handleSend(qr.label)}
                    className="text-left text-xs px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:bg-white/[0.10] hover:border-white/20 transition-all duration-150"
                  >
                    {qr.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input row */}
            <div className="px-4 pb-4 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question..."
                  className="flex-1 bg-white/[0.06] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-sky-500/50 focus:bg-white/[0.09] transition-all"
                />
                <button
                  onClick={() => handleSend(inputText)}
                  disabled={!inputText.trim()}
                  className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow-sky disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Powered by Claude badge */}
              <p className="mt-2 text-center text-[10px] text-white/30">
                Powered by{' '}
                <span className="text-white/50 font-medium">Claude</span>
                {' '}· AI responses may vary
              </p>
            </div>
          </GlassPanel>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* FAB toggle button                                                   */}
      {/* ------------------------------------------------------------------ */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'fixed bottom-6 right-6 z-50',
          'w-14 h-14 rounded-full',
          'bg-gradient-to-br from-primary to-accent',
          'shadow-glow-sky shadow-lg',
          'flex items-center justify-center',
          'transition-all duration-300 hover:scale-110 active:scale-95',
          isOpen && 'rotate-90'
        )}
        aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>
    </>
  )
}
